/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/ActionPage';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { messageType } from '../../registry/constants';
import { defaultValue } from './ActionShowMessage';

const ActionShowMessageManager: ActionManagerFn<'ActionShowMessage'> = (
  platform: Platform,
): ActionManager<'ActionShowMessage'> => {
  return {
    ...baseActionManager(platform, 'ActionShowMessage'),
    defaultValue,
    props: {
      ...baseActionProps,
      message: {
        label: 'Message',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      type: {
        label: 'Type',
        type: 'select',
        options: messageType,
        required: true,
      },
      timeoutInSec: {
        label: 'Time Out In Seconds',
        type: 'number',
        min: 0,
      },
    },
    name: 'Show Message',
    getDisplayText: (item) => item.props.message.value,
    icon: (item) => ({
      iconName: 'info-circle',
      color: item.props.type,
    }),
    description: `**Overview**<br/>
The Show Message action is a user feedback component designed to display transient notifications to the user. Functioning primarily as a "Toast" or "Snackbar," it provides a non-intrusive way to communicate system states, operation results, or alerts without interrupting the user's workflow with a blocking modal dialog.<br/><br/>
This component is essential for maintaining a responsive user experience. It allows the application to acknowledge user actions (like saving a form) or report issues (like a network error) using standard visual cues that users immediately recognize.

**Key Features**<br/>
* **Semantic Typing:** Categorizes messages into four distinct types—Info, Success, Warning, and Error—automatically applying appropriate color schemes (e.g., Green for Success, Red for Error) to convey meaning instantly.
* **Dynamic Content:** The message property is an expression, allowing developers to construct personalized feedback strings at runtime (e.g., "Order #123 has been submitted successfully").
* **Auto-Dismissal:** Features a timeoutInSec property to control how long the message remains visible, ensuring that old notifications do not clutter the screen.
* **Non-Blocking:** displays messages typically at the top or bottom of the viewport, allowing users to continue interacting with the rest of the application while the message is visible.

**Properties**<br/>
* **Message (Required):** An expression property returning a string.
  * *Function:* The actual text content to display.
  * *Example:* 'Record saved successfully!' or 'Error: ' + #{errorMsg}.
* **Type (Required):** A selection property.
  * *Options:* Info, Warning, Error, Success.
  * *Function:* Determines the visual theme and icon of the message bubble.
* **Time Out In Seconds:** A numeric property.
  * *Function:* Defined in seconds how long the message stays on screen. If set to 0 or left blank, the default system timeout is usually applied.

**Use Cases**<br/>
* **Success Confirmation:** Triggering a green "Success" message immediately after an ActionSave completes to confirm the data was stored.
* **Validation Feedback:** Showing a red "Error" message if a custom script detects invalid data that standard validators missed.
* **Process Status:** Displaying a blue "Info" message saying "Export started..." when a user initiates a background report generation.
* **System Alerts:** showing a yellow "Warning" message if the user tries to navigate away from a page with unsaved changes.`,

    helpRenders: [
      {
        title: 'Action Show Message',
        description:
          'Try changing the props and press the below button to see it in action!',
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['type', 'message'],
        item: {
          children: [],
          itemId: 'actionShowMessage',
          parent: 'triggerOnClick',
          props: {
            message: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Hi ${$.session.displayName}!`',
            },
            type: 'info',
          },
          type: 'ActionShowMessage',
        },
      },
    ],
  };
};

export default ActionShowMessageManager;
